import { useMemo } from "react";

import {
  useLocationTimezone,
  useLocationTimezoneOf,
  useTimezone,
} from "@/hooks/use-timezone";

import { ClinicClock, clinicClockFor } from "@/lib/clinic-clock";

/** The clinic clock, re-bound whenever the effective timezone changes. */
export function useClinicClock(): ClinicClock {
  const timeZone = useTimezone();
  return useMemo(() => clinicClockFor(timeZone), [timeZone]);
}

/** The clock of one location — an appointment's, a register's. */
export function useLocationClock(
  locationId: string | null | undefined,
): ClinicClock {
  const timeZone = useLocationTimezone(locationId);
  return useMemo(() => clinicClockFor(timeZone), [timeZone]);
}

/**
 * Each appointment's own location clock, falling back to the clinic clock
 * while locations load or when the appointment has none. For lists that mix
 * locations, so "today" means today where that visit happens.
 */
export function useAppointmentClockOf(): (appointment: {
  location?: { id?: string | null } | null;
}) => ClinicClock {
  const clinicClock = useClinicClock();
  const timezoneOf = useLocationTimezoneOf();
  return useMemo(() => {
    const clocks = new Map<string, ClinicClock>();
    return (appointment) => {
      // Without a location, the lookup would answer with the browser's zone.
      const locationId = appointment.location?.id;
      if (!locationId) return clinicClock;
      const timeZone = timezoneOf(locationId);
      if (!timeZone || timeZone === clinicClock.timeZone) return clinicClock;
      let clock = clocks.get(timeZone);
      if (!clock) {
        clock = clinicClockFor(timeZone);
        clocks.set(timeZone, clock);
      }
      return clock;
    };
  }, [clinicClock, timezoneOf]);
}
